import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { Form, Col, Modal, Row } from "react-bootstrap";
import { Formik } from "formik";
import { get } from "lodash";

import Alert from "components/Alert";
import Button from "components/Button";

import { flushErrors } from "actions/errorActions";
import { createLoadingSelector } from "store/selectors";
import { createErrorSelector } from "store/selectors";
import { createOrganizationForCompany } from "actions/adminActions";
import { toast } from "react-toastify";
import { adminConstants } from "actions/types";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import * as yup from "yup";
import type { RootState } from "types/state";

const schema = yup.object({
  name: yup
    .string()
    .label("Organization Name")
    .required()
    .min(2, "Must be at least two characters.")
    .max(100),
});

const orgEvent = adminConstants.CREATE_ORGANIZATION;

const loadingSelector = createLoadingSelector(orgEvent);
const errorSelector = createErrorSelector(orgEvent);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: loadingSelector(state),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  flushErrors,
  createOrganizationForCompany,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

// Own props supplied by the parent (Organization); the submit path fires a
// dispatched mutation and then calls the parent's onSuccess callback.
interface OwnProps {
  companyId: string;
  onClose: () => void;
  onSuccess: (organization: unknown) => void;
}

type Props = WithApolloClient<ConnectedProps<typeof connector> & OwnProps>;

interface FormValues {
  name: string;
}

class AddOrgModal extends React.PureComponent<Props> {
  _onClose = () => {
    this.props.flushErrors();
    this.props.onClose();
  };

  _submitOrganization = async ({ name }: FormValues) => {
    const res = await this.props.createOrganizationForCompany(
      this.props.client as ApolloClient<object>,
      {
        companyId: this.props.companyId,
        name,
      }
    );
    if (res.success) {
      const organization = get(res, "data.organization");
      toast.success("Successfully created organization.");
      this.props.onSuccess(organization);
    }
  };

  render() {
    return (
      <Formik<FormValues>
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={(values) => {
          void this._submitOrganization(values);
        }}
        enableReinitialize={true}
        initialValues={{
          name: "",
        }}
      >
        {({ handleSubmit, handleChange, values, touched, errors }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Modal
              centered
              scrollable
              show={true}
              onHide={this._onClose}
              className="icon-modal"
            >
              <Modal.Header closeButton>Create Organization</Modal.Header>

              <Modal.Body>
                <Row className="mb-3">
                  <Form.Group as={Col} controlId="formGroupName">
                    <Form.Label>
                      What name do you want to give the organization?
                    </Form.Label>
                    <Form.Control
                      name="name"
                      placeholder="Organization Name"
                      value={values.name}
                      onChange={handleChange}
                      isInvalid={!!errors.name}
                      isValid={touched.name && !errors.name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
                {this.props.error && (
                  <Alert type="error" msg={this.props.error} />
                )}
              </Modal.Body>
              <Modal.Footer>
                <div className="submit-row">
                  <Button
                    btnLabel="Create Organization"
                    name="submit"
                    withArrow={true}
                    loading={this.props.isFetching}
                    onClick={() =>
                      // @ts-expect-error Formik's handleSubmit expects an
                      // optional FormEvent; master passes `values` here, which
                      // Formik ignores at runtime. Preserved as-is.
                      handleSubmit(values)
                    }
                  />
                  <Button
                    btnLabel="Cancel"
                    color="cancel"
                    name="cancel"
                    type="button"
                    onClick={this.props.onClose}
                  />
                </div>
              </Modal.Footer>
            </Modal>
          </Form>
        )}
      </Formik>
    );
  }
}

export default connector(withApollo<Props>(AddOrgModal));
